import { type UIEvent, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Search } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useGetDevicePage } from "@/hooks/api/use-get-device-page"
import { useAuth } from "@/hooks/useAuth"
import { TableDateDisplay } from "@/components/ui/date-display"
import { DeviceDTOTypeEnum } from "@/gen/api"
import { formatDeviceType } from "@/lib/deviceType"

export default function DevicesPage() {
  const navigate = useNavigate()
  const { councilId: councilIdUrl } = useParams()
  const { user } = useAuth()
  const councilId = councilIdUrl || user?.councils?.[0]?.id?.toString()
  const [searchQuery, setSearchQuery] = useState("")
  const [deviceTypeFilter, setDeviceTypeFilter] = useState("all")
  const [alarmFilter, setAlarmFilter] = useState("all")

  const deviceType = deviceTypeFilter === "all" ? undefined : deviceTypeFilter
  const status = undefined

  // Backend search only matches platformId/id, not device name (name comes from platform
  // services at runtime). Fetch all devices and filter client-side by name/serial/id.
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useGetDevicePage(
    councilId ?? "",
    "",
    status,
    undefined,
    deviceType,
  )

  // When a client-side filter (alarm or search) is active, we need all pages loaded
  // to filter accurately, so auto-fetch until no more pages.
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return
    if (alarmFilter !== "all" || searchQuery) {
      fetchNextPage()
    }
  }, [alarmFilter, searchQuery, fetchNextPage, hasNextPage, isFetchingNextPage, data?.pages.length])

  const devices = (data?.pages.flatMap((page) => page.devices) ?? []).filter((device) => {
    if (alarmFilter === "active" && !((device.leakAlarms ?? 0) > 0)) return false
    if (alarmFilter === "none" && !((device.leakAlarms ?? 0) === 0)) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      const name = (device.name ?? "").toLowerCase()
      const serial = (device.serialNumber ?? "").toLowerCase()
      const id = (device.id ?? "").toString().toLowerCase()
      if (!name.includes(q) && !serial.includes(q) && !id.includes(q)) return false
    }
    return true
  })

  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) return

    const target = event.currentTarget
    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight
    if (distanceToBottom < 120) {
      fetchNextPage()
    }
  }

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col space-y-6 overflow-hidden">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Devices</h1>
          <p className="text-muted-foreground">
            Shows device status across platforms and leak alarm counts
          </p>
        </div>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col">
        <CardHeader className="shrink-0">
          <CardTitle>Device Management</CardTitle>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                placeholder="Search device by serial, name, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <div className="flex gap-2">
              <Select value={deviceTypeFilter} onValueChange={setDeviceTypeFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by Device type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Devices</SelectItem>
                  <SelectItem value={DeviceDTOTypeEnum.SmartMeter}>Smart Meters</SelectItem>
                  <SelectItem value={DeviceDTOTypeEnum.DataLogger}>Data Loggers</SelectItem>
                  <SelectItem value={DeviceDTOTypeEnum.Unknown}>Unknown</SelectItem>
                </SelectContent>
              </Select>

              <Select value={alarmFilter} onValueChange={setAlarmFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by Alarm" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active Alarms</SelectItem>
                  <SelectItem value="none">No Alarms</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="min-h-0 flex-1 overflow-hidden">
          <div className="h-full space-y-4 overflow-y-auto pr-2" onScroll={handleListScroll}>
            {devices.map((device) => (
              <div key={device.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Device → {device.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Serial Number: {device.serialNumber}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Type → {formatDeviceType(device.type)}
                    </p>
                  </div>
                  <div className="text-right space-y-2">
                    <div>
                      <Badge variant={(device.leakAlarms ?? 0) > 0 ? "destructive" : "secondary"}>
                        {device.leakAlarms} Leak Alarms
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Last Updated: <TableDateDisplay date={device.lastUpdated ?? null} />
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate(`${councilId ? `/council/${councilId}` : ""}/devices/${device.id}`)
                      }
                    >
                      Open
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {isLoading && devices.length === 0 && (
              <>
                {["device-loading-1", "device-loading-2", "device-loading-3"].map((key) => (
                  <div key={key} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between gap-6">
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-2/3" />
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-4 w-40" />
                      </div>
                      <div className="space-y-2 text-right">
                        <Skeleton className="ml-auto h-6 w-28 rounded-full" />
                        <Skeleton className="ml-auto h-4 w-44" />
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <Skeleton className="h-9 w-16" />
                    </div>
                  </div>
                ))}
              </>
            )}

            {!isLoading && devices.length === 0 && (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No devices found matching your criteria.</p>
              </div>
            )}

            {isFetchingNextPage && (
              <div className="py-2 text-center text-sm text-muted-foreground">
                Loading more devices...
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
